import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, ChevronDown, ChevronRight, ClipboardList, HelpCircle, Mail,
  Pencil, Shield, Trash2, Truck, UserCheck, UserPlus, Users, X,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { describeInviteOutcome, InviteUserResult, useOrgAdminUsers } from '../../hooks/useOrgAdminUsers';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { ConfirmModal } from '../../components/admin/ConfirmModal';
import { CustomDropdown } from '../../components/ui/CustomDropdown';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { SuccessToast } from '../../components/ui/SuccessToast';
import { OrgUser } from '../../types/admin';

// ── Role metadata ──────────────────────────────────────────────────────────

const ROLE_INFO: Record<string, {
  label: string;
  shortDesc: string;
  summary: string;
  access: string[];
  capabilities: string[];
  badgeClass: string;
  icon: typeof Shield;
}> = {
  admin: {
    label: 'Admin',
    shortDesc: 'Full organizational access',
    summary: 'Full access to all features, settings, and user management.',
    access: ['All pages including Admin Panel, Dashboard, and Operations'],
    capabilities: [
      'Manage users, roles, and company settings',
      'View and manage all quotes, jobs, and leads',
      'Manage fleet (warehouses and trucks)',
      'View financials and payment notifications',
      'Configure integrations and feature flags',
    ],
    badgeClass: 'bg-plum text-white',
    icon: Shield,
  },
  field_lead: {
    label: 'Field Lead',
    shortDesc: 'Jack-of-all-trades operations lead',
    summary: 'Can do everything an Estimator, Dispatcher, or Mover can do — plus full access to Operations, Dispatch, Schedule, and Fleet views. Cannot access the Admin Panel, user management, billing, or org settings.',
    access: ['Sales, Operations, Dispatch, Schedule, Fleet, My Moves'],
    capabilities: [
      'View and work all quotes, leads, and jobs (assigned or unassigned)',
      'Auto-assigned or manually assigned as Quote Lead (estimator work)',
      'Can be assigned to crews as a Move Lead or Mover',
      'Full access to Dispatch, Schedule, and Fleet views',
      'Counts toward both mover and estimator capacity in scheduling',
      'Cannot access the Admin Panel, manage users, or manage billing',
    ],
    badgeClass: 'bg-emerald-100 text-emerald-700',
    icon: Users,
  },
  dispatcher: {
    label: 'Dispatcher',
    shortDesc: 'Schedules and coordinates operations',
    summary: 'Manages scheduling, crew assignment, and job coordination.',
    access: ['Sales, Operations, My Moves'],
    capabilities: [
      'Assign crews and trucks to jobs',
      'Manage job scheduling and dispatch',
      'Receive job status and quote booking notifications',
    ],
    badgeClass: 'bg-amber-100 text-amber-700',
    icon: ClipboardList,
  },
  estimator: {
    label: 'Estimator',
    shortDesc: 'Creates and manages quotes',
    summary: 'Handles the sales process — creates quotes, runs the formula, and sends proposals to customers.',
    access: ['Sales, My Moves'],
    capabilities: [
      'Create and edit quotes',
      'Run the estimation formula',
      'Generate and send quote versions to customers',
      'Manage leads and follow-ups',
    ],
    badgeClass: 'bg-stone text-midnight',
    icon: ClipboardList,
  },
  mover: {
    label: 'Mover',
    shortDesc: 'Executes jobs in the field',
    summary: 'Field crew member who executes moves. Only sees jobs they are assigned to.',
    access: ['My Moves (assigned jobs only)'],
    capabilities: [
      'View assigned jobs and job details',
      'Advance job stages (en route, loading, etc.)',
      'Log issues and capture customer sign-offs',
      'Check trucks in and out',
    ],
    badgeClass: 'bg-blue-100 text-blue-700',
    icon: Truck,
  },
};

// ── Role Overview Sheet ────────────────────────────────────────────────────

function RoleOverviewSheet({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8
                      md:rounded-2xl md:max-w-lg md:w-full md:pb-6 md:max-h-[85vh] md:overflow-y-auto
                      max-h-[85vh] overflow-y-auto">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Role Overview</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p className="text-small text-slate mb-5 leading-relaxed">
          Each role controls what pages a user can see, what actions they can take, and what notifications they receive.
        </p>

        <div className="flex flex-col gap-4">
          {['admin', 'field_lead', 'dispatcher', 'estimator', 'mover'].map(roleKey => {
            const info = ROLE_INFO[roleKey];
            const Icon = info.icon;
            return (
              <div key={roleKey} className="border border-stone rounded-xl p-4">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${info.badgeClass}`}>
                    <Icon size={14} />
                  </div>
                  <div>
                    <span className="font-semibold text-small text-midnight">{info.label}</span>
                    <span className="block text-[12px] text-slate leading-snug">{info.shortDesc}</span>
                  </div>
                </div>
                <p className="text-[13px] text-midnight/80 leading-relaxed mb-3">{info.summary}</p>

                <div className="mb-2">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate">Access</span>
                  <ul className="mt-1">
                    {info.access.map((a, i) => (
                      <li key={i} className="text-[12px] text-midnight/70 leading-snug">
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate">Capabilities</span>
                  <ul className="mt-1 flex flex-col gap-0.5">
                    {info.capabilities.map((c, i) => (
                      <li key={i} className="text-[12px] text-midnight/70 leading-snug flex gap-1.5">
                        <span className="text-plum mt-px shrink-0">•</span>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Role options builder ───────────────────────────────────────────────────

function buildRoleOptions(opts: { jobsEnabled: boolean; dispatchEnabled: boolean }) {
  return [
    { value: 'estimator', label: 'Estimator', description: ROLE_INFO.estimator.shortDesc },
    { value: 'admin', label: 'Admin', description: ROLE_INFO.admin.shortDesc },
    { value: 'field_lead', label: 'Field Lead', description: ROLE_INFO.field_lead.shortDesc },
    ...(opts.jobsEnabled ? [{ value: 'mover', label: 'Mover', description: ROLE_INFO.mover.shortDesc }] : []),
    ...(opts.dispatchEnabled ? [{ value: 'dispatcher', label: 'Dispatcher', description: ROLE_INFO.dispatcher.shortDesc }] : []),
  ];
}

// ── Avatar helpers ──────────────────────────────────────────────────────────

const AVATAR_COLORS = ['bg-plum', 'bg-rose', 'bg-midnight', 'bg-slate'];
function avatarColor(uid: string): string {
  const code = uid.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// ── Invite User form (bottom sheet) ────────────────────────────────────────

interface InviteFormProps {
  onClose: () => void;
  onInvite: (email: string, role: OrgUser['role']) => Promise<InviteUserResult>;
  forceAdmin?: boolean;
  dispatchEnabled?: boolean;
  jobsEnabled?: boolean;
}

function InviteForm({ onClose, onInvite, forceAdmin, dispatchEnabled = true, jobsEnabled = true }: InviteFormProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgUser['role']>(forceAdmin ? 'admin' : 'estimator');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await onInvite(email.trim(), role);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite.');
      setSubmitting(false);
    }
  }

  return (
    /* Mobile: bottom sheet. md+: centered dialog. */
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Invite User</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="invite-email"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
            >
              Email Address <span className="text-rose">*</span>
            </label>
            <input
              id="invite-email"
              type="email"
              placeholder="user@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                         text-small text-midnight placeholder:text-slate
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors duration-150"
            />
          </div>

          <div>
            <label
              htmlFor="invite-role"
              className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1"
            >
              Role
            </label>
            <CustomDropdown
              id="invite-role"
              value={role}
              onChange={val => setRole(val as OrgUser['role'])}
              options={buildRoleOptions({ jobsEnabled, dispatchEnabled })}
              placeholder="Select role…"
            />
            {forceAdmin && (
              <p className="text-[12px] text-amber-700 mt-1.5 leading-snug">
                This organization has no active admin. The first invited user must be an Admin.
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="h-11 rounded-lg bg-plum text-white text-small font-semibold
                       hover:bg-rose active:opacity-80 disabled:opacity-50
                       transition-colors duration-150"
          >
            {submitting ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── User row (with inline edit) ─────────────────────────────────────────────

interface UserRowProps {
  user: OrgUser;
  isSelf: boolean;
  isSoleAdmin: boolean;
  onUpdateRole: (uid: string, role: OrgUser['role']) => Promise<OrgUser>;
  onRemove: (user: OrgUser) => void;
  onResendInvite?: (uid: string) => Promise<void>;
  dispatchEnabled?: boolean;
  jobsEnabled?: boolean;
}

function UserRow({ user, isSelf, isSoleAdmin, onUpdateRole, onRemove, onResendInvite, dispatchEnabled = true, jobsEnabled = true }: UserRowProps) {
  const [editing, setEditing] = useState(false);
  const [soleAdminMsg, setSoleAdminMsg] = useState(false);
  const [role, setRole] = useState<OrgUser['role']>(user.role);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const isInvited = user.status === 'invited';

  function handleEditClick() {
    if (isSoleAdmin) {
      setSoleAdminMsg(true);
      return;
    }
    setEditing(true);
    setRole(user.role);
    setError('');
    setSoleAdminMsg(false);
  }

  async function handleSaveRole() {
    setSaving(true);
    setError('');
    try {
      await onUpdateRole(user.uid, role);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role.');
    }
    setSaving(false);
  }

  return (
    <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
      {/* Top row: avatar + info + edit button */}
      <div className="flex items-center gap-3">
        {isInvited ? (
          <div className="shrink-0 w-10 h-10 rounded-full bg-stone/60 flex items-center justify-center">
            <UserPlus size={16} className="text-slate" />
          </div>
        ) : user.photoBase64 ? (
          <img
            src={user.photoBase64}
            alt={user.displayName || user.email}
            className="shrink-0 w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div
            className={`shrink-0 w-10 h-10 rounded-full ${avatarColor(user.uid)} flex items-center justify-center`}
          >
            <span className="text-white font-display font-bold text-sm">
              {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isInvited ? (
              <span className="text-sm italic text-slate/70 font-normal">invited</span>
            ) : (
              <span className="font-semibold text-small text-midnight truncate">
                {user.displayName || user.email}
              </span>
            )}
            {!editing && (
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold
                            uppercase tracking-wide leading-none
                            ${user.role === 'admin' ? 'bg-plum text-white' : user.role === 'mover' ? 'bg-blue-100 text-blue-700' : user.role === 'dispatcher' ? 'bg-amber-100 text-amber-700' : user.role === 'field_lead' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone text-midnight'}`}
              >
                {user.role === 'admin' ? 'Admin' : user.role === 'mover' ? 'Mover' : user.role === 'dispatcher' ? 'Dispatcher' : user.role === 'field_lead' ? 'Field Lead' : 'Estimator'}
              </span>
            )}
          </div>
          <p className="text-[13px] text-slate mt-0.5 truncate">{user.email}</p>
        </div>

        {!editing ? (
          <button
            type="button"
            onClick={handleEditClick}
            className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:border-plum hover:text-plum active:bg-mist
                       transition-colors duration-150"
            aria-label="Edit user"
          >
            <Pencil size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-stone
                       text-slate hover:text-midnight active:bg-mist transition-colors duration-150"
            aria-label="Cancel edit"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Resend Invite button for invited users */}
      {isInvited && !editing && onResendInvite && (
        <div className="mt-3">
          <button
            type="button"
            disabled={resending || resendSuccess}
            onClick={async () => {
              setResending(true);
              setError('');
              try {
                await onResendInvite(user.uid);
                setResendSuccess(true);
                setTimeout(() => setResendSuccess(false), 3000);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to resend invite.');
              }
              setResending(false);
            }}
            className="h-9 px-3 rounded-lg border border-plum text-plum text-[13px] font-semibold
                       flex items-center gap-1.5
                       hover:bg-plum/5 active:bg-plum/10 disabled:opacity-50
                       transition-colors duration-150"
          >
            <Mail size={14} />
            {resendSuccess ? 'Invite Sent!' : resending ? 'Sending…' : 'Resend Invite'}
          </button>
          {error && <p className="text-[13px] text-rose mt-1.5">{error}</p>}
        </div>
      )}

      {/* Sole-admin notice */}
      {soleAdminMsg && !editing && (
        <div className="mt-3 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 text-[13px] text-amber-800 leading-snug">
          Your org only has one Admin. To edit this user, assign the Admin role to at least one other user.
        </div>
      )}

      {/* Inline edit panel */}
      {editing && (
        <div className="mt-4 pt-3 border-t border-stone flex flex-col gap-3">
          {error && <p className="text-[13px] text-rose">{error}</p>}

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
              Role
            </label>
            <CustomDropdown
              value={role}
              onChange={val => setRole(val as OrgUser['role'])}
              options={buildRoleOptions({ jobsEnabled, dispatchEnabled })}
              placeholder="Select role…"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onRemove(user)}
              disabled={isSelf}
              className="h-10 px-4 rounded-lg border border-rose text-rose text-[13px] font-semibold
                         hover:bg-rose/5 active:bg-rose/10 disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors duration-150"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={handleSaveRole}
              disabled={saving || role === user.role}
              className="flex-1 h-10 rounded-lg bg-plum text-white text-[13px] font-semibold
                         flex items-center justify-center gap-1.5
                         hover:bg-rose active:opacity-80 disabled:opacity-50
                         transition-colors duration-150"
            >
              {saving ? 'Saving…' : <><Check size={14} />Save Role</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inner page (requires orgId) ─────────────────────────────────────────────

function OrgManageUsersContent({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { currentUser } = useUserProfile();
  const { users, loading, error, inviteUser, updateRole, removeUser, activateUser, resendInvite } = useOrgAdminUsers(orgId);
  const { org } = useOrgSettings(orgId);
  const [isInviting, setIsInviting] = useState(false);
  const [showUserLimit, setShowSeatLimit] = useState(false);
  const [showRoleOverview, setShowRoleOverview] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<OrgUser | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState('');
  const dismissToast = useCallback(() => setToastMessage(''), []);

  // Invite via the hook, then tell the admin what happened: a brand-new invite,
  // or an existing member (active / invited / inactive) that was reused.
  const handleInvite = useCallback(async (email: string, role: OrgUser['role']) => {
    const result = await inviteUser(email, role);
    setToastMessage(describeInviteOutcome(result));
    return result;
  }, [inviteUser]);

  const removingInvite = pendingRemove?.status === 'invited';
  const removingNeverActivated = removingInvite || pendingRemove?.neverActivated === true;
  // Members occupying a seat: everyone who is not inactive. The plan limit
  // counts pending invites too (the backend's invite / activate cap uses the
  // same rule), so the seat line below must as well. The headings, however,
  // count active and invited rows separately so "Active Users" is honest.
  const seatedUsers = users.filter(u => u.status !== 'inactive');
  const activeUsers = users.filter(u => u.status === 'active');
  const invitedUsers = users.filter(u => u.status === 'invited');
  const inactiveUsers = users.filter(u => u.status === 'inactive');

  const maxUsers = org?.maxUsers ?? null;
  const seatCount = seatedUsers.length;
  const hasCapacity = maxUsers === null || seatCount < maxUsers;
  const dispatchEnabled = org?.featureDispatch !== false;
  const jobsEnabled = org?.featureJobs !== false;

  const adminCount = seatedUsers.filter(u => u.role === 'admin').length;
  const hasActiveAdmin = seatedUsers.some(u => u.role === 'admin' && u.status === 'active');

  async function handleRemoveConfirm() {
    if (!pendingRemove) return;
    setRemoveError(null);
    const target = pendingRemove;
    try {
      const { deleted } = await removeUser(target.uid);
      setPendingRemove(null);
      setToastMessage(deleted
        ? `Invite for ${target.email} removed.`
        : `${target.displayName || target.email} deactivated.`);
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove user.');
    }
  }

  async function handleActivate(user: OrgUser) {
    setActivateError(null);
    try {
      await activateUser(user.uid);
    } catch (err) {
      setActivateError(err instanceof Error ? err.message : 'Failed to activate user.');
    }
  }

  return (
    <>
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="text-slate hover:text-midnight transition-colors duration-150"
          >
            Admin Panel
          </button>
          <ChevronRight size={14} className="text-stone" />
          <button
            type="button"
            onClick={() => navigate('/admin/company')}
            className="text-slate hover:text-midnight transition-colors duration-150"
          >
            Company
          </button>
          <ChevronRight size={14} className="text-stone" />
          <span className="text-midnight font-medium">Manage Users</span>
        </nav>

        <h1 className="font-display text-h1 text-midnight mb-1">Manage Users</h1>
        <p className="text-small text-slate mb-6">Manage your company's members.</p>

        {/* Invite button */}
        <button
          type="button"
          onClick={() => hasCapacity ? setIsInviting(true) : setShowSeatLimit(true)}
          className="w-full h-11 rounded-xl bg-plum text-white text-small font-semibold
                     flex items-center justify-center gap-2
                     hover:bg-rose active:opacity-80 transition-colors duration-150 mb-2"
        >
          <UserPlus size={18} />
          Invite User
        </button>

        <button
          type="button"
          onClick={() => setShowRoleOverview(true)}
          className="flex items-center justify-center gap-1.5 w-full text-[13px] text-slate
                     hover:text-plum transition-colors duration-150 mb-6"
        >
          <HelpCircle size={14} />
          Role Overview
        </button>

        {removeError && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
            {removeError}
          </div>
        )}

        {activateError && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
            {activateError}
          </div>
        )}

        {/* Users list */}
        {loading && (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center py-16 gap-2 text-center">
            <p className="text-small text-rose font-semibold">Failed to load users</p>
            <p className="text-small text-slate">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate">
                Active Users ({activeUsers.length})
              </p>
              {maxUsers !== null && (
                <p className="text-[12px] text-slate tabular-nums">
                  {seatCount} of {maxUsers} seat{maxUsers === 1 ? '' : 's'} used
                  {invitedUsers.length > 0 && ' (incl. pending invites)'}
                </p>
              )}
            </div>
            {activeUsers.length === 0 ? (
              <div className="flex flex-col items-center py-16 gap-2 text-center">
                <p className="text-small text-slate">
                  {invitedUsers.length > 0
                    ? 'No active users yet. Pending invites are listed below.'
                    : 'No members yet. Invite your first user above.'}
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-3">
                {activeUsers.map(user => (
                  <li key={user.uid}>
                    <UserRow
                      user={user}
                      isSelf={user.uid === currentUser?.uid}
                      isSoleAdmin={user.role === 'admin' && adminCount === 1}
                      onUpdateRole={updateRole}
                      onRemove={u => { setRemoveError(null); setPendingRemove(u); }}
                      onResendInvite={resendInvite}
                      dispatchEnabled={dispatchEnabled}
                      jobsEnabled={jobsEnabled}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* Invited (not yet accepted) section */}
            {invitedUsers.length > 0 && (
              <div className="mt-6">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">
                  Invited ({invitedUsers.length})
                </p>
                <ul className="flex flex-col gap-3">
                  {invitedUsers.map(user => (
                    <li key={user.uid}>
                      <UserRow
                        user={user}
                        isSelf={user.uid === currentUser?.uid}
                        isSoleAdmin={user.role === 'admin' && adminCount === 1}
                        onUpdateRole={updateRole}
                        onRemove={u => { setRemoveError(null); setPendingRemove(u); }}
                        onResendInvite={resendInvite}
                        dispatchEnabled={dispatchEnabled}
                        jobsEnabled={jobsEnabled}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Inactive Users section */}
            {inactiveUsers.length > 0 && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowInactive(prev => !prev)}
                  className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate
                             hover:text-midnight transition-colors duration-150 mb-3"
                >
                  {showInactive ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  Inactive Users ({inactiveUsers.length})
                </button>
                {showInactive && (
                  <ul className="flex flex-col gap-3">
                    {inactiveUsers.map(user => (
                      <li key={user.uid}>
                        <div className="bg-white/60 border border-stone rounded-xl p-4 shadow-brand-sm opacity-75">
                          <div className="flex items-center gap-3">
                            <div
                              className={`shrink-0 w-10 h-10 rounded-full bg-stone/60 flex items-center justify-center`}
                            >
                              <span className="text-slate font-display font-bold text-sm">
                                {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-small text-slate truncate">
                                  {user.displayName || user.email}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide leading-none bg-stone/50 text-slate">
                                  Inactive
                                </span>
                              </div>
                              <p className="text-[13px] text-slate/70 mt-0.5 truncate">{user.email}</p>
                            </div>
                            {user.neverActivated && (
                              <button
                                type="button"
                                onClick={() => { setRemoveError(null); setPendingRemove(user); }}
                                className="shrink-0 h-9 px-3 rounded-lg border border-rose text-rose text-[13px] font-semibold
                                           flex items-center gap-1.5
                                           hover:bg-rose/5 active:bg-rose/10 transition-colors duration-150"
                                aria-label={`Permanently remove ${user.email}`}
                              >
                                <Trash2 size={14} />
                                Remove
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleActivate(user)}
                              className="shrink-0 h-9 px-3 rounded-lg border border-plum text-plum text-[13px] font-semibold
                                         flex items-center gap-1.5
                                         hover:bg-plum/5 active:bg-plum/10 transition-colors duration-150"
                            >
                              <UserCheck size={14} />
                              Activate
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {showRoleOverview && <RoleOverviewSheet onClose={() => setShowRoleOverview(false)} />}

      {isInviting && (
        <InviteForm onClose={() => setIsInviting(false)} onInvite={handleInvite} forceAdmin={!hasActiveAdmin} dispatchEnabled={dispatchEnabled} jobsEnabled={jobsEnabled} />
      )}

      <ConfirmModal
        isOpen={!!pendingRemove}
        title={removingInvite ? 'Remove invite?' : removingNeverActivated ? 'Permanently remove?' : 'Deactivate user?'}
        message={removingInvite
          ? `The pending invite for ${pendingRemove?.email} will be deleted. You can invite them again later.`
          : removingNeverActivated
            ? `${pendingRemove?.email} never activated their account. This record will be permanently deleted.`
            : `${pendingRemove?.displayName || pendingRemove?.email} will be deactivated and will no longer be able to access your organization. They can be reactivated later.`}
        confirmLabel={removingInvite ? 'Remove Invite' : removingNeverActivated ? 'Remove' : 'Deactivate'}
        variant="danger"
        onConfirm={handleRemoveConfirm}
        onClose={() => setPendingRemove(null)}
      />

      <ConfirmModal
        isOpen={showUserLimit}
        title="User limit reached"
        message={`Your organization has reached its limit of ${maxUsers} user${maxUsers === 1 ? '' : 's'}. To invite a new user, upgrade your plan or deactivate an existing user.`}
        confirmLabel="Got it"
        variant="primary"
        onConfirm={() => setShowSeatLimit(false)}
        onClose={() => setShowSeatLimit(false)}
      />

      {toastMessage && (
        <SuccessToast message={toastMessage} onDismiss={dismissToast} durationMs={5000} />
      )}
    </>
  );
}

// ── Page (guards org admin access) ─────────────────────────────────────────

export function OrgManageUsersPage() {
  const { adminOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!adminOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have org admin access.</p>
      </div>
    );
  }

  return <OrgManageUsersContent orgId={adminOrg.orgId} />;
}
